import { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Search, Mail, Phone, Briefcase, Calendar, Users as UsersIcon } from 'lucide-react';
import { AddStaffModal } from '../modals/AddStaffModal';
import { api } from '../../lib/api';

interface StaffManagementProps {
  restaurantId: string;
}

export function StaffManagement({ restaurantId }: StaffManagementProps) {
  const [staff, setStaff] = useState<any[]>([]);
  const [departmentOptions, setDepartmentOptions] = useState<Array<{ id: string; name: string }>>([]);
  const [addStaffModalOpen, setAddStaffModalOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('all');
  const [toastMessage, setToastMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Load staff data
  useEffect(() => {
    loadStaffData();
    loadDepartmentData();
  }, [restaurantId]);

  const normalizeRole = (role: string) => String(role || '').trim().toUpperCase().replace(/[\s-]+/g, '_');

  const loadStaffData = async () => {
    try {
      setErrorMessage('');
      const response = await api.users.getAll({
        page: 1,
        limit: 100,
        restaurantId,
      });
      const userRows = Array.isArray(response.data) ? response.data : response.data?.users || [];
      const disallowedRoles = ['SUPER_ADMIN', 'OWNER', 'RESTAURANT_ADMIN'];

      const mapped = userRows
        .filter((u: any) =>
          !disallowedRoles.includes(normalizeRole(u.role || u.roleName)) &&
          String(u.restaurantId || '') === String(restaurantId || '')
        )
        .map((u: any) => ({
          id: u.id,
          name: u.name,
          email: u.email,
          phone: u.phone || '-',
          role: normalizeRole(u.role || u.roleName),
          position: u.roleName || u.role || 'Staff',
          department: u.departmentName || 'Unassigned',
          departmentId: u.departmentId || null,
          joiningDate: u.joiningDate || u.joining_date || '',
          shift: u.shift || 'morning',
          employeeId: String(u.id),
          restaurantId: u.restaurantId || restaurantId,
          isActive: u.isActive !== false,
          avatar: u.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${u.email || u.name || u.id}`,
          createdAt: u.createdAt || new Date().toISOString(),
          updatedAt: u.updatedAt || new Date().toISOString(),
        }));

      setStaff(mapped);
    } catch (error) {
      console.error('Failed to load staff data:', error);
      setErrorMessage('Failed to load staff from database');
      setStaff([]);
    }
  };

  const loadDepartmentData = async () => {
    try {
      const response = await api.masterData.departments.getAll({ isActive: true });
      const rows = Array.isArray(response.data) ? response.data : [];
      setDepartmentOptions(rows.map((d: any) => ({ id: d.id, name: d.name })));
    } catch (error) {
      console.error('Failed to load department data:', error);
      setDepartmentOptions([]);
    }
  };

  const handleAddStaffSuccess = (message: string) => {
    setToastMessage(message);
    setTimeout(() => setToastMessage(''), 5001);
    loadStaffData();
  };

  const handleEditStaff = (staffMember: any) => {
    setEditingStaff(staffMember);
    setAddStaffModalOpen(true);
  };

  const handleDeleteStaff = async (id: string) => {
    if (confirm('Are you sure you want to delete this staff member?')) {
      try {
        await api.users.delete(id);
        await loadStaffData();
        setToastMessage('Staff member deleted successfully');
        setTimeout(() => setToastMessage(''), 5001);
      } catch (error) {
        console.error('Failed to delete staff:', error);
        setErrorMessage('Failed to delete staff member');
      }
    }
  };

  const handleToggleStaffStatus = async (id: string) => {
    try {
      const target = staff.find((s) => s.id === id);
      if (!target) return;
      await api.users.update(id, { isActive: !target.isActive });
      await loadStaffData();
      setToastMessage('Staff status updated');
      setTimeout(() => setToastMessage(''), 5001);
    } catch (error) {
      console.error('Failed to update staff status:', error);
      setErrorMessage('Failed to update staff status');
    }
  };

  const filteredStaff = staff.filter(s => {
    const matchesSearch = s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(s.employeeId).toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDepartment = filterDepartment === 'all' || s.department?.toLowerCase() === filterDepartment;
    return matchesSearch && matchesDepartment;
  });

  const departments = [
    'all',
    ...Array.from(
      new Set(
        departmentOptions
          .map((d) => String(d.name || '').trim())
          .filter(Boolean)
      )
    ),
  ];

  return (
    <div>
      {/* Toast Notification */}
      {toastMessage && (
        <div className="fixed top-4 right-4 z-50 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg animate-slide-in-right">
          {toastMessage}
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl text-gray-900 font-bold">Staff Management</h1>
          <p className="text-gray-600 text-sm mt-1">Manage your restaurant team members</p>
        </div>
        <button
          onClick={() => {
            setEditingStaff(null);
            setAddStaffModalOpen(true);
          }}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add Staff Member
        </button>
      </div>

      {errorMessage && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {errorMessage}
        </div>
      )}

      {/* Search and Filter */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search staff by name, email, or ID..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <select
            value={filterDepartment}
            onChange={(e) => setFilterDepartment(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {departments.map((dept, index) => (
              <option key={`${dept.toLowerCase()}-${index}`} value={dept.toLowerCase()}>
                {dept === 'all' ? 'All Departments' : dept}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Stats Summary */}
      {staff.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm p-4">
            <p className="text-gray-600 text-sm">Total Staff</p>
            <p className="text-2xl font-bold text-gray-900">{staff.length}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <p className="text-gray-600 text-sm">Active</p>
            <p className="text-2xl font-bold text-green-600">{staff.filter(s => s.isActive).length}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <p className="text-gray-600 text-sm">Managers</p>
            <p className="text-2xl font-bold text-blue-600">{staff.filter(s => s.role === 'MANAGER').length}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <p className="text-gray-600 text-sm">Kitchen Staff</p>
            <p className="text-2xl font-bold text-purple-600">{staff.filter(s => s.department?.toLowerCase() === 'kitchen').length}</p>
          </div>
        </div>
      )}

      {/* Staff Grid */}
      {filteredStaff.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredStaff.map((staffMember) => (
            <div key={staffMember.id} className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <img
                    src={staffMember.avatar}
                    alt={staffMember.name}
                    className="w-12 h-12 rounded-full"
                  />
                  <div>
                    <h4 className="text-gray-900 font-semibold">{staffMember.name}</h4>
                    <p className="text-gray-600 text-sm">{staffMember.position}</p>
                  </div>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  staffMember.isActive
                    ? 'bg-green-100 text-green-700'
                    : 'bg-gray-100 text-gray-700'
                }`}>
                  {staffMember.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>

              <div className="space-y-2 mb-4">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Mail className="w-4 h-4" />
                  <span className="truncate">{staffMember.email}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Phone className="w-4 h-4" />
                  <span>{staffMember.phone}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Briefcase className="w-4 h-4" />
                  <span>{staffMember.department}</span>
                </div>
              </div>

              <div className="flex gap-2 pt-4 border-t border-gray-200">
                <button
                  onClick={() => handleEditStaff(staffMember)}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors"
                >
                  <Edit className="w-4 h-4" />
                  Edit
                </button>
                <button
                  onClick={() => handleToggleStaffStatus(staffMember.id)}
                  className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm"
                >
                  {staffMember.isActive ? 'Deactivate' : 'Activate'}
                </button>
                <button
                  onClick={() => handleDeleteStaff(staffMember.id)}
                  className="px-3 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center">
          <UsersIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-gray-900 font-semibold mb-2">No Staff Members Found</h3>
          <p className="text-gray-600 mb-6">
            {searchTerm || filterDepartment !== 'all'
              ? 'Try adjusting your search or filters'
              : 'Get started by adding your first staff member'}
          </p>
          {!searchTerm && filterDepartment === 'all' && (
            <button
              onClick={() => setAddStaffModalOpen(true)}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Add First Staff Member
            </button>
          )}
        </div>
      )}

      {/* Add Staff Modal */}
      <AddStaffModal
        isOpen={addStaffModalOpen}
        onClose={() => {
          setAddStaffModalOpen(false);
          setEditingStaff(null);
        }}
        onSuccess={handleAddStaffSuccess}
        restaurantId={restaurantId}
        editingStaff={editingStaff}
      />
    </div>
  );
}
